import { useTranslation } from 'react-i18next';
import { FileText, TrendingUp, Coffee } from 'lucide-react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';

const ReportHighlight = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section className="py-32 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-8 tracking-tight">
            {t('report.title')}
          </h2>
          <p className="text-2xl md:text-3xl font-bold text-blue-300 max-w-4xl mx-auto">
            {t('report.subtitle')}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
          {/* 左側：説明（ジグザグレイアウト用に順序を変更可能） */}
          <motion.div 
            ref={ref}
            className="space-y-8 order-2 md:order-1"
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
            <motion.div 
              className="flex items-start gap-6"
              variants={itemVariants}
              whileHover={{ x: 10 }}
              transition={{ duration: 0.2 }}
            >
              <motion.div 
                className="bg-blue-500 rounded-2xl p-4 flex-shrink-0"
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ duration: 0.2 }}
              >
                <FileText size={32} className="text-white" />
              </motion.div>
              <div>
                <h3 className="text-3xl font-black text-white mb-3">
                  {t('report.features.automatic.title')}
                </h3>
                <p className="text-xl text-gray-300 leading-relaxed">
                  {t('report.features.automatic.description')}
                </p>
              </div>
            </motion.div>

            <motion.div 
              className="flex items-start gap-6"
              variants={itemVariants}
              whileHover={{ x: 10 }}
              transition={{ duration: 0.2 }}
            >
              <motion.div 
                className="bg-cyan-500 rounded-2xl p-4 flex-shrink-0"
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ duration: 0.2 }}
              >
                <TrendingUp size={32} className="text-white" />
              </motion.div>
              <div>
                <h3 className="text-3xl font-black text-white mb-3">
                  {t('report.features.insights.title')}
                </h3>
                <p className="text-xl text-gray-300 leading-relaxed">
                  {t('report.features.insights.description')}
                </p>
              </div>
            </motion.div>

            <motion.div 
              className="flex items-start gap-6"
              variants={itemVariants}
              whileHover={{ x: 10 }}
              transition={{ duration: 0.2 }}
            >
              <motion.div 
                className="bg-green-500 rounded-2xl p-4 flex-shrink-0"
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ duration: 0.2 }}
              >
                <Coffee size={32} className="text-white" />
              </motion.div>
              <div>
                <h3 className="text-3xl font-black text-white mb-3">
                  {t('report.features.efficiency.title')}
                </h3>
                <p className="text-xl text-gray-300 leading-relaxed">
                  {t('report.features.efficiency.description')}
                </p>
              </div>
            </motion.div>
          </motion.div>

          {/* 右側：レポートサンプル（プレースホルダー） */}
          <motion.div 
            className="bg-white rounded-3xl p-8 shadow-2xl order-1 md:order-2 border-2 border-blue-100"
            variants={cardVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            whileHover={{ scale: 1.02, y: -5 }}
            transition={{ duration: 0.2 }}
          >
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 mb-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-2xl font-black text-gray-900">
                  {t('report.sample.title')}
                </h4>
                <span className="bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-bold">
                  {t('report.sample.status')}
                </span>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600 font-semibold">{t('report.sample.customer')}</span>
                  <span className="text-gray-900 font-bold">John Smith</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 font-semibold">{t('report.sample.interest')}</span>
                  <span className="text-gray-900 font-bold">High</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 font-semibold">{t('report.sample.budget')}</span>
                  <span className="text-gray-900 font-bold">$50,000 - $100,000</span>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-600 font-semibold mb-2">{t('report.sample.nextAction')}</p>
                  <p className="text-gray-900 font-bold">{t('report.sample.actionDetail')}</p>
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-500 text-center">
              {t('report.sample.note')}
            </p>
          </motion.div>
        </div>

        <motion.div 
          className="text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
        >
          <p className="text-3xl font-black text-white mb-4">
            {t('report.cta.title')}
          </p>
          <p className="text-xl text-gray-300">
            {t('report.cta.description')}
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default ReportHighlight;

